import { List } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../../components/ui/popover";
import { useColumnActionLogs } from "../../../../../hooks/useColumnActionLogs";
import { ActivityLogList } from "./ActivityLogList";

interface ActivityPopoverProps {
  /** The result whose column-action-log feed to show. Absent → the button still
   * renders but the feed stays empty (nothing to fetch). */
  attributeTestResultId?: string;
}

/**
 * The trail pane's "Activity" button + popover, surfacing this result's
 * column-action-log feed (generations, pass/fail changes, edits).
 *
 * Self-contained on purpose: it owns the fetch with `fetchOnLoad: false` and
 * (re)loads on each open, so (a) browsing cells doesn't fire a GET per cell for
 * data behind a rarely-opened popover, and (b) an edit made elsewhere in the
 * fold-out — which doesn't remount this per-cell — shows the moment the popover
 * is reopened, instead of going stale until navigation.
 */
export function ActivityPopover({
  attributeTestResultId,
}: ActivityPopoverProps) {
  const { logs, loading, fetchLogs } = useColumnActionLogs({
    attributeTestResultId,
    fetchOnLoad: false,
  });

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) fetchLogs();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded border border-stone-200 px-2 py-1 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-100"
        >
          <List className="h-3.5 w-3.5" />
          Activity
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="max-h-72 overflow-y-auto overscroll-contain">
          <ActivityLogList activityLogs={logs} logsLoading={loading} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
